import { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Server,
  Plus,
  Circle,
  Search,
  ChevronDown,
  PlayCircle,
  StopCircle,
  Zap,
  Copy,
  Check,
  Trash2,
  Key,
  Loader2,
  Container,
  Activity,
  RefreshCw,
  DollarSign,
} from 'lucide-react';
import clsx from 'clsx';
import { CreateDeploymentModal } from '../components';
import { apiKeysApi, billingApi, GpuContainer } from '../services/api';

interface DeploymentFunction {
  id: string;
  name: string;
  description?: string;
  gpuType: string;
  endpoint?: string;
  isActive: boolean;
}

interface Deployment {
  id: string;
  name: string;
  slug: string;
  description?: string;
  type: 'DEDICATED' | 'CONTAINER';
  gpuType: string;
  status: 'STOPPED' | 'STARTING' | 'RUNNING' | 'STOPPING' | 'ERROR' | 'IDLE';
  instanceCount: number;
  minInstances: number;
  maxInstances: number;
  autoScale: boolean;
  workspace: {
    id: string;
    name: string;
    slug: string;
  };
  functions: DeploymentFunction[];
  createdAt: string;
  lastDeployedAt?: string;
  // Enhanced fields matching Workspace structure
  deploymentType?: 'instance' | 'container';
  infrastructureId?: string;
  infrastructureStatus?: 'creating' | 'running' | 'stopped' | 'error' | 'terminating';
  role?: 'OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER';
}

export function AppsPage() {
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'running' | 'stopped'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedDeployment, setExpandedDeployment] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // API Key management state
  const [showApiKeyModal, setShowApiKeyModal] = useState<string | null>(null);
  const [apiKeyData, setApiKeyData] = useState<{
    key?: string;
    keyPrefix?: string;
    masked?: string;
    isNew?: boolean;
    gpuContainerName?: string;
    syncedToInfrastructure?: boolean;
  } | null>(null);
  const [loadingApiKey, setLoadingApiKey] = useState(false);
  const [copiedApiKey, setCopiedApiKey] = useState(false);
  
  // GPU Container selection state
  const [gpuContainers, setGpuContainers] = useState<GpuContainer[]>([]);
  const [loadingContainers, setLoadingContainers] = useState(false);
  const [selectedContainer, setSelectedContainer] = useState<string>('');
  const [apiKeyName, setApiKeyName] = useState('');
  const [creatingKey, setCreatingKey] = useState(false);
  const [showContainerSelector, setShowContainerSelector] = useState(false);

  // Live cost metering state
  const [activeResourceCosts, setActiveResourceCosts] = useState<Record<string, { ratePerSecond: number; totalCostUsd: number; startedAt: number }>>({});
  const [liveCosts, setLiveCosts] = useState<Record<string, number>>({});
  const liveCostRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    fetchDeployments();
  }, []);

  // Fetch active metering data and start live cost ticker
  const fetchActiveResources = useCallback(async () => {
    try {
      const data = await billingApi.getActiveResources();
      const costs: Record<string, { ratePerSecond: number; totalCostUsd: number; startedAt: number }> = {};
      for (const d of data.deployments) {
        costs[d.id] = { ratePerSecond: d.ratePerSecond, totalCostUsd: d.totalCostUsd, startedAt: d.meteringStartedAt ? new Date(d.meteringStartedAt).getTime() : Date.now() };
      }
      setActiveResourceCosts(costs);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const hasRunning = deployments.some(d => d.status === 'RUNNING');
    if (hasRunning) {
      fetchActiveResources();
      const refreshInterval = setInterval(fetchActiveResources, 30000);
      return () => clearInterval(refreshInterval);
    }
  }, [deployments, fetchActiveResources]);

  // 1-second live cost ticker for running deployments
  useEffect(() => {
    if (liveCostRef.current) clearInterval(liveCostRef.current);
    const entries = Object.entries(activeResourceCosts);
    if (entries.length === 0) return;

    const tick = () => {
      const now = Date.now();
      const updated: Record<string, number> = {};
      for (const [id, data] of entries) {
        const elapsed = (now - data.startedAt) / 1000;
        updated[id] = data.totalCostUsd + (elapsed > 0 ? data.ratePerSecond * Math.max(0, elapsed - (data.totalCostUsd / data.ratePerSecond)) : 0);
        // Simpler: approximate as totalCost + ratePerSecond * (now - fetchTime)
        updated[id] = Math.round((data.totalCostUsd + data.ratePerSecond * ((now - data.startedAt) / 1000)) * 10000) / 10000;
      }
      setLiveCosts(updated);
    };
    tick();
    liveCostRef.current = setInterval(tick, 1000);
    return () => { if (liveCostRef.current) clearInterval(liveCostRef.current); };
  }, [activeResourceCosts]);

  // Auto-poll infrastructure status for deployments stuck in 'creating' state
  // This actively queries the infrastructure status for current deployments
  useEffect(() => {
    const creatingDeployments = deployments.filter(
      (d) => d.infrastructureStatus === 'creating' && d.infrastructureId
    );
    
    if (creatingDeployments.length === 0) return;
    
    const pollInterval = setInterval(async () => {
      let statusChanged = false;
      for (const deployment of creatingDeployments) {
        try {
          // Poll workspace infrastructure status (since deployments are actually workspaces)
          const infraResponse = await fetch(`/api/workspaces/${deployment.id}/infrastructure`, {
            credentials: 'include',
          });
          if (infraResponse.ok) {
            const infraData = await infraResponse.json();
            const newStatus = infraData.data?.infrastructureStatus;
            if (newStatus && newStatus !== deployment.infrastructureStatus) {
              statusChanged = true;
            }
          }
        } catch {
          // Ignore polling errors
        }
      }
      // If any status changed, refresh the full deployment list
      if (statusChanged) {
        fetchDeployments();
      }
    }, 8000); // Poll every 8 seconds
    
    return () => clearInterval(pollInterval);
  }, [deployments]);

  const fetchDeployments = async () => {
    try {
      // Fetch workspaces which contain the GPU infrastructure (instances and containers)
      const response = await fetch('/api/workspaces', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        // Transform workspaces to match deployment interface
        // Only show workspaces that have infrastructure (GPU instances/containers)
        const workspacesWithInfra = data.data
          .filter((ws: any) => ws.deploymentType) // Only show workspaces with GPU infrastructure
          .map((ws: any) => ({
            id: ws.id,
            name: ws.name,
            slug: ws.slug,
            type: ws.deploymentType === 'instance' ? 'DEDICATED' : 'CONTAINER',
            gpuType: ws.infrastructureConfig?.instanceType || 'g4dn.xlarge',
            status: ws.infrastructureStatus === 'running' ? 'RUNNING' : 
                   ws.infrastructureStatus === 'creating' ? 'STARTING' :
                   ws.infrastructureStatus === 'stopped' ? 'STOPPED' :
                   ws.infrastructureStatus === 'error' ? 'ERROR' : 'STOPPED',
            instanceCount: ws.infrastructureConfig?.desiredCount || ws.infrastructureConfig?.maxInstances || 1,
            minInstances: ws.infrastructureConfig?.minInstances || 0,
            maxInstances: ws.infrastructureConfig?.maxInstances || ws.infrastructureConfig?.maxInstances || 1,
            autoScale: ws.infrastructureConfig?.autoScale || (ws.deploymentType === 'container'),
            workspace: {
              id: ws.id,
              name: ws.name,
              slug: ws.slug,
            },
            functions: [], // Workspaces don't have functions yet, could be added later
            createdAt: ws.createdAt,
            deploymentType: ws.deploymentType,
            infrastructureId: ws.infrastructureId,
            infrastructureStatus: ws.infrastructureStatus,
            role: ws.role,
          }));
        
        setDeployments(workspacesWithInfra);
      }
    } catch (error) {
      console.error('Failed to fetch deployments:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchDeployments();
  };

  const handleStatusChange = async (deploymentId: string, newStatus: string) => {
    // Note: Status changes for GPU infrastructure are managed automatically
    // This is a placeholder for future implementation of manual start/stop
    alert('Infrastructure status is managed automatically by the system.\n\nGPU instances will auto-scale and terminate based on your configuration.');
    return;
    
    /* Future implementation:
    try {
      const response = await fetch(`/api/workspaces/${deploymentId}/infrastructure/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        fetchDeployments();
      }
    } catch (error) {
      console.error('Failed to update deployment status:', error);
    }
    */
  };

  const copyToClipboard = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDeleteDeployment = async (deploymentId: string) => {
    setDeletingId(deploymentId);
    try {
      const deployment = deployments.find(d => d.id === deploymentId);
      // Delete workspace (since deployments are actually workspaces with infrastructure)
      const response = await fetch(`/api/workspaces/${deploymentId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (response.ok) {
        setShowDeleteConfirm(null);
        
        // Show success message
        if (deployment?.infrastructureId || deployment?.deploymentType) {
          alert('Deployment deleted successfully.\n\nGPU infrastructure cleanup has been initiated and will complete in the background.');
        } else {
          alert('Deployment deleted successfully.');
        }
        
        fetchDeployments();
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      console.error('Failed to delete deployment:', error);
      alert('Failed to delete deployment. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleGetApiKey = async (deploymentId: string) => {
    setShowApiKeyModal(deploymentId);
    setApiKeyData(null);
    setCopiedApiKey(false);
    setShowContainerSelector(false);
    setSelectedContainer('');
    setApiKeyName('');
    setLoadingApiKey(true);

    try {
      // First try to get existing API key for this deployment
      const response = await apiKeysApi.getWorkspaceKey(deploymentId);
      
      // Check if API key exists
      if (!response.exists || !response.apiKey) {
        // No key exists, show container selector to create a new one
        setShowContainerSelector(true);
        // Fetch GPU containers from infrastructure
        setLoadingContainers(true);
        try {
          const containersResponse = await apiKeysApi.getGpuContainers();
          setGpuContainers(containersResponse.gpuContainers || []);
          // Find deployment name for default API key name
          const deployment = deployments.find(d => d.id === deploymentId);
          setApiKeyName(deployment ? `${deployment.name} API Key` : 'GPU Deployment API Key');
        } catch (containerError) {
          console.error('Failed to fetch GPU containers:', containerError);
          setGpuContainers([]);
        } finally {
          setLoadingContainers(false);
        }
      } else {
        // API key exists, show it
        setApiKeyData({
          keyPrefix: response.apiKey.keyPrefix,
          masked: response.apiKey.masked,
          isNew: false,
          gpuContainerName: (response.apiKey as any).gpuContainerName,
        });
      }
    } catch (error: any) {
      // Handle actual errors (network issues, server errors, etc.)
      console.error('Failed to fetch API key:', error);
      alert('Failed to fetch API key. Please try again.');
      setShowApiKeyModal(null);
    } finally {
      setLoadingApiKey(false);
    }
  };

  const handleCreateApiKey = async () => {
    if (!showApiKeyModal || !apiKeyName.trim()) return;
    
    setCreatingKey(true);
    try {
      const selectedContainerObj = gpuContainers.find(c => c.id === selectedContainer);
      
      // Use the deployment-specific API key creation endpoint
      const createResponse = await apiKeysApi.createWorkspaceKey(showApiKeyModal, {
        gpuContainerId: selectedContainer || undefined,
        gpuContainerName: selectedContainerObj?.name || undefined,
      });
      
      setApiKeyData({
        key: createResponse.apiKey.key,
        keyPrefix: createResponse.apiKey.keyPrefix,
        isNew: true,
        gpuContainerName: selectedContainerObj?.name,
        syncedToInfrastructure: createResponse.apiKey.syncedToInfrastructure,
      });
      setShowContainerSelector(false);
    } catch (error) {
      console.error('Failed to create API key:', error);
      alert('Failed to create API key. Please try again.');
    } finally {
      setCreatingKey(false);
    }
  };

  const copyApiKey = (key: string) => {
    navigator.clipboard.writeText(key);
    setCopiedApiKey(true);
    setTimeout(() => setCopiedApiKey(false), 2000);
  };

  const filteredDeployments = deployments.filter((deployment) => {
    const matchesFilter =
      filter === 'all' ||
      (filter === 'running' && deployment.status === 'RUNNING') ||
      (filter === 'stopped' && deployment.status === 'STOPPED');

    const matchesSearch =
      searchTerm === '' ||
      deployment.name.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  const getStatusColor = (status: Deployment['status']) => {
    switch (status) {
      case 'RUNNING':
        return 'text-green-500';
      case 'STARTING':
        return 'text-yellow-500';
      case 'STOPPED':
        return 'text-gray-400';
      case 'STOPPING':
        return 'text-orange-500';
      case 'ERROR':
        return 'text-red-500';
      case 'IDLE':
        return 'text-blue-500';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusText = (status: Deployment['status']) => {
    switch (status) {
      case 'RUNNING':
        return 'Live';
      case 'STOPPED':
        return 'Stopped';
      case 'IDLE':
        return 'Currently idle';
      default:
        return status;
    }
  };

  const runningCount = deployments.filter((d) => d.status === 'RUNNING').length;
  const stoppedCount = deployments.filter((d) => d.status === 'STOPPED').length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Apps & Deployments</h1>
          <p className="mt-1 text-sm text-foreground-secondary">
            Manage your GPU deployments, infrastructure, and API access.
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg hover:bg-background-hover transition-colors disabled:opacity-50 text-sm"
            title="Refresh deployments"
          >
            <RefreshCw className={clsx('w-4 h-4', isRefreshing && 'animate-spin')} />
            <span className="hidden sm:inline">{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors text-sm"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Create Deployment</span>
            <span className="sm:hidden">New</span>
          </button>
        </div>
      </div>

      {/* Stats & Search Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 px-3 py-2 bg-background-secondary border border-border rounded-lg">
            <Activity className="w-4 h-4 text-green-500" />
            <span className="text-sm font-medium text-foreground">{runningCount}</span>
            <span className="text-xs text-foreground-secondary">Running</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 bg-background-secondary border border-border rounded-lg">
            <Circle className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-foreground">{stoppedCount}</span>
            <span className="text-xs text-foreground-secondary">Stopped</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 bg-background-secondary border border-border rounded-lg">
            <Server className="w-4 h-4 text-accent" />
            <span className="text-sm font-medium text-foreground">{deployments.length}</span>
            <span className="text-xs text-foreground-secondary">Total</span>
          </div>
        </div>

        <div className="w-full sm:flex-1 sm:max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-foreground-tertiary" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search deployments by name..."
              className="w-full pl-10 pr-4 py-2 bg-background-secondary border border-border rounded-md text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </div>
        </div>
      </div>

      {/* Deployments Table */}
      <div className="bg-background-secondary border border-border rounded-lg overflow-hidden overflow-x-auto">
        <table className="w-full min-w-0">
          <thead>
            <tr className="border-b border-border bg-background-tertiary">
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-foreground-secondary uppercase tracking-wider">
                Deployment
              </th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-foreground-secondary uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-foreground-secondary uppercase tracking-wider hidden md:table-cell">
                Infrastructure
              </th>
              <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-foreground-secondary uppercase tracking-wider hidden sm:table-cell">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredDeployments.map((deployment) => (
              <tr
                key={deployment.id}
                className="hover:bg-background-hover transition-colors"
              >
                <td className="px-4 sm:px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      {deployment.deploymentType === 'container' ? (
                        <Container className="w-5 h-5 text-accent" />
                      ) : (
                        <Server className="w-5 h-5 text-accent" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">
                          {deployment.name}
                        </span>
                        <span
                          className={clsx(
                            'px-2 py-0.5 text-xs rounded-full',
                            deployment.type === 'DEDICATED'
                              ? 'bg-purple-500/10 text-purple-500'
                              : 'bg-blue-500/10 text-blue-500'
                          )}
                        >
                          {deployment.type === 'DEDICATED' ? 'dedicated' : 'auto-scaling'}
                        </span>
                      </div>
                      <div className="text-xs text-foreground-tertiary mt-1 flex items-center gap-2">
                        <span>{deployment.gpuType} GPU</span>
                        {deployment.deploymentType && (
                          <>
                            <span>·</span>
                            <span className="inline-flex items-center gap-1">
                              {deployment.deploymentType === 'instance' ? '🖥️ GPU Instance' : '📦 GPU Container'}
                            </span>
                          </>
                        )}
                        {deployment.functions.length > 0 && (
                          <>
                            <span>·</span>
                            <button
                              onClick={() =>
                                setExpandedDeployment(
                                  expandedDeployment === deployment.id ? null : deployment.id
                                )
                              }
                              className="text-accent hover:underline"
                            >
                              {deployment.functions.length} function{deployment.functions.length !== 1 ? 's' : ''}
                            </button>
                          </>
                        )}
                      </div>
                      {expandedDeployment === deployment.id && deployment.functions.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {deployment.functions.map((func) => (
                            <div
                              key={func.id}
                              className="flex items-center gap-2 text-xs p-2 bg-background rounded"
                            >
                              <Circle
                                className={clsx(
                                  'w-2 h-2',
                                  func.isActive
                                    ? 'text-green-500 fill-green-500'
                                    : 'text-gray-400 fill-gray-400'
                                )}
                              />
                              <span className="text-foreground">{func.name}</span>
                              {func.endpoint && (
                                <code className="ml-auto px-2 py-0.5 bg-background-tertiary text-accent rounded text-xs">
                                  {func.endpoint}
                                </code>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 sm:px-6 py-4">
                  <div className="flex flex-col gap-1">
                    <span
                      className={clsx(
                        'inline-flex items-center gap-1.5 text-xs font-medium w-fit',
                        getStatusColor(deployment.status)
                      )}
                    >
                      <Circle className="w-2 h-2 fill-current" />
                      {getStatusText(deployment.status)}
                    </span>
                    {deployment.infrastructureStatus && (
                      <span
                        className={clsx(
                          'px-1.5 py-0.5 text-xs rounded w-fit',
                          deployment.infrastructureStatus === 'running'
                            ? 'bg-green-500/10 text-green-500'
                            : deployment.infrastructureStatus === 'creating'
                            ? 'bg-yellow-500/10 text-yellow-500 animate-pulse'
                            : deployment.infrastructureStatus === 'error'
                            ? 'bg-red-500/10 text-red-500'
                            : deployment.infrastructureStatus === 'terminating'
                            ? 'bg-orange-500/10 text-orange-500'
                            : 'bg-gray-500/10 text-gray-400'
                        )}
                      >
                        Infra: {deployment.infrastructureStatus}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 sm:px-6 py-4 hidden md:table-cell">
                  <div className="text-xs text-foreground-secondary space-y-1">
                    {deployment.status === 'RUNNING' && activeResourceCosts[deployment.id] && (
                      <div className="flex items-center gap-1 text-green-400 font-mono">
                        <DollarSign className="w-3 h-3" />
                        <span>{(liveCosts[deployment.id] ?? 0).toFixed(4)}</span>
                      </div>
                    )}
                    {deployment.status === 'RUNNING' && activeResourceCosts[deployment.id] && (
                      <div className="text-foreground-tertiary">
                        ${activeResourceCosts[deployment.id].ratePerSecond.toFixed(6)}/sec
                      </div>
                    )}
                    {deployment.autoScale && (
                      <div className="flex items-center gap-1">
                        <Zap className="w-3 h-3 text-yellow-500" />
                        <span>Scale: {deployment.minInstances}-{deployment.maxInstances}</span>
                      </div>
                    )}
                    <div>
                      Instances: {deployment.instanceCount}
                    </div>
                    <div className="text-foreground-tertiary">
                      Created {new Date(deployment.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </div>
                  </div>
                </td>
                <td className="px-4 sm:px-6 py-4 text-right hidden sm:table-cell">
                  <div className="flex items-center justify-end gap-2 flex-wrap">
                    {/* Status Control Buttons */}
                    {deployment.status === 'STOPPED' ? (
                      <button
                        onClick={() => handleStatusChange(deployment.id, 'RUNNING')}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-green-500 border border-green-500/30 rounded-md hover:bg-green-500/10 transition-colors"
                        title="Start deployment"
                      >
                        <PlayCircle className="w-3 h-3" />
                        Start
                      </button>
                    ) : deployment.status === 'RUNNING' ? (
                      <button
                        onClick={() => handleStatusChange(deployment.id, 'STOPPED')}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-orange-500 border border-orange-500/30 rounded-md hover:bg-orange-500/10 transition-colors"
                        title="Stop deployment"
                      >
                        <StopCircle className="w-3 h-3" />
                        Stop
                      </button>
                    ) : null}
                    
                    {/* API Key Button - Show for running deployments with infrastructure */}
                    {deployment.deploymentType && deployment.infrastructureStatus === 'running' && (
                      <button
                        onClick={() => handleGetApiKey(deployment.id)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-accent hover:text-accent/80 border border-accent/20 rounded-md hover:bg-accent/10 transition-colors"
                        title="Manage API Key"
                      >
                        <Key className="w-3 h-3" />
                        API Key
                      </button>
                    )}
                    
                    {/* Copy ID */}
                    <button
                      onClick={() => copyToClipboard(deployment.id)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-foreground-secondary hover:text-foreground border border-border rounded-md hover:bg-background-hover transition-colors"
                      title="Copy Deployment ID"
                    >
                      {copiedId === deployment.id ? (
                        <>
                          <Check className="w-3 h-3 text-green-500" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          Copy ID
                        </>
                      )}
                    </button>
                    
                    {/* Delete Button - Show for owners */}
                    {(!deployment.role || deployment.role === 'OWNER' || deployment.role === 'ADMIN') && (
                      <button
                        onClick={() => setShowDeleteConfirm(deployment.id)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-500 hover:text-red-600 border border-red-500/20 rounded-md hover:bg-red-500/10 transition-colors"
                        disabled={deletingId === deployment.id}
                        title="Delete deployment"
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredDeployments.length === 0 && (
          <div className="text-center py-12">
            <Server className="w-12 h-12 text-foreground-tertiary mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-1">
              No deployments found
            </h3>
            <p className="text-sm text-foreground-secondary">
              {searchTerm
                ? 'Try adjusting your search'
                : 'Create your first GPU deployment to get started'}
            </p>
          </div>
        )}
      </div>

      {/* Create Deployment Modal */}
      <CreateDeploymentModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={fetchDeployments}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background-secondary border border-border rounded-lg p-6 w-full max-w-md relative z-10 shadow-2xl">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              Delete Deployment
            </h2>
            <p className="text-foreground-secondary mb-6">
              Are you sure you want to delete this deployment? This action cannot be undone.
              {(() => {
                const deployment = deployments.find(d => d.id === showDeleteConfirm);
                const hasInfra = deployment?.infrastructureId || deployment?.deploymentType;
                return hasInfra ? (
                  <span className="block mt-3 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-md">
                    <span className="flex items-center gap-2 text-yellow-500 font-medium">
                      <Server className="w-4 h-4" />
                      GPU Infrastructure Detected
                    </span>
                    <span className="block mt-1 text-sm text-foreground-tertiary">
                      This deployment has associated GPU infrastructure that will be automatically deleted along with all resources (instances, containers, networks, security groups, etc.).
                    </span>
                  </span>
                ) : null;
              })()}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border border-border rounded-md text-foreground-secondary hover:bg-background-hover transition-colors"
                disabled={deletingId === showDeleteConfirm}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteDeployment(showDeleteConfirm)}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={deletingId === showDeleteConfirm}
              >
                {deletingId === showDeleteConfirm ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Deleting...
                  </span>
                ) : (
                  'Delete Deployment'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Key Modal - Using Portal to render at document body level */}
      {showApiKeyModal && createPortal(
        <div 
          className="fixed inset-0 bg-black/70 flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
          onClick={() => setShowApiKeyModal(null)}
        >
          <div 
            className="bg-background-secondary border border-border rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
            style={{ zIndex: 10000 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <Key className="w-5 h-5 text-accent" />
                Deployment API Key
              </h2>
              <button
                onClick={() => setShowApiKeyModal(null)}
                className="text-foreground-secondary hover:text-foreground text-xl"
              >
                ✕
              </button>
            </div>

            {loadingApiKey ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
              </div>
            ) : showContainerSelector ? (
              /* GPU Container Selection UI */
              <div className="space-y-4">
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                  <p className="text-blue-400 text-sm font-medium mb-1">
                    Create a new API key for this deployment
                  </p>
                  <p className="text-foreground-secondary text-sm">
                    Select a GPU container to associate with this API key. The key will be synced with the infrastructure.
                  </p>
                </div>

                {/* API Key Name */}
                <div>
                  <label className="block text-sm font-medium text-foreground-secondary mb-2">
                    API Key Name
                  </label>
                  <input
                    type="text"
                    value={apiKeyName}
                    onChange={(e) => setApiKeyName(e.target.value)}
                    placeholder="Enter a name for this API key"
                    className="w-full bg-background-tertiary border border-border rounded-lg px-4 py-3 text-sm text-foreground placeholder-foreground-tertiary focus:outline-none focus:ring-2 focus:ring-accent/50"
                  />
                </div>

                {/* GPU Container Selector */}
                <div>
                  <label className="block text-sm font-medium text-foreground-secondary mb-2">
                    GPU Container (Optional)
                  </label>
                  {loadingContainers ? (
                    <div className="flex items-center gap-2 text-foreground-secondary py-3">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading GPU containers...
                    </div>
                  ) : gpuContainers.length > 0 ? (
                    <div className="space-y-2">
                      <select
                        value={selectedContainer}
                        onChange={(e) => setSelectedContainer(e.target.value)}
                        className="w-full bg-background-tertiary border border-border rounded-lg px-4 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent/50"
                      >
                        <option value="">No specific container (general key)</option>
                        {gpuContainers.map((container) => (
                          <option key={container.id} value={container.id}>
                            {container.name} ({container.status}) - {container.runningTasksCount} tasks running
                          </option>
                        ))}
                      </select>
                      {selectedContainer && (
                        <div className="bg-background-tertiary border border-border rounded-lg p-3">
                          <div className="flex items-center gap-2 text-sm">
                            <Server className="w-4 h-4 text-accent" />
                            <span className="text-foreground font-medium">
                              {gpuContainers.find(c => c.id === selectedContainer)?.name}
                            </span>
                          </div>
                          <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-foreground-secondary">
                            <div>Tasks: {gpuContainers.find(c => c.id === selectedContainer)?.runningTasksCount || 0}</div>
                            <div>Instances: {gpuContainers.find(c => c.id === selectedContainer)?.registeredContainerInstancesCount || 0}</div>
                            <div>Services: {gpuContainers.find(c => c.id === selectedContainer)?.activeServicesCount || 0}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-foreground-tertiary text-sm py-3">
                      No GPU containers available. You can still create a general API key.
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowApiKeyModal(null)}
                    className="flex-1 px-4 py-2 border border-border rounded-lg text-foreground-secondary hover:bg-background-hover transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateApiKey}
                    disabled={!apiKeyName.trim() || creatingKey}
                    className="flex-1 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {creatingKey ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Key className="w-4 h-4" />
                        Create API Key
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : apiKeyData ? (
              /* Display API Key */
              <div className="space-y-4">
                {apiKeyData.isNew && (
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                    <p className="text-yellow-500 text-sm font-medium mb-1">
                      ⚠️ Important: Save this API key now!
                    </p>
                    <p className="text-foreground-secondary text-sm">
                      This is the only time you'll see the full API key. Make sure to copy and store it securely.
                    </p>
                    {apiKeyData.syncedToInfrastructure && (
                      <p className="text-green-500 text-sm mt-2">
                        ✓ This key has been synced with the infrastructure.
                      </p>
                    )}
                  </div>
                )}

                {apiKeyData.gpuContainerName && (
                  <div className="flex items-center gap-2 text-sm bg-background-tertiary border border-border rounded-lg p-3">
                    <Server className="w-4 h-4 text-accent" />
                    <span className="text-foreground-secondary">Associated GPU Container:</span>
                    <span className="text-foreground font-medium">{apiKeyData.gpuContainerName}</span>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-foreground-secondary mb-2">
                    API Key
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-background-tertiary border border-border rounded-lg px-4 py-3 font-mono text-sm text-foreground overflow-x-auto">
                      {apiKeyData.key || apiKeyData.masked || apiKeyData.keyPrefix}
                    </div>
                    {apiKeyData.key && (
                      <button
                        onClick={() => copyApiKey(apiKeyData.key!)}
                        className="px-4 py-3 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors flex items-center gap-2 whitespace-nowrap"
                      >
                        {copiedApiKey ? (
                          <>
                            <Check className="w-4 h-4" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            Copy
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>

                <div className="bg-background-tertiary border border-border rounded-lg p-4 space-y-3">
                  <h3 className="text-sm font-medium text-foreground">How to use this API key</h3>
                  <div className="space-y-2 text-sm text-foreground-secondary">
                    <p>Use this API key to authenticate requests to your GPU deployment:</p>
                    <div className="bg-background border border-border rounded p-3 font-mono text-xs overflow-x-auto">
                      <div className="text-green-400"># Using curl</div>
                      <div className="mt-1">
                        curl -X POST https://your-deployment.gpu.com/execute \<br />
                        &nbsp;&nbsp;-H "Authorization: Bearer {apiKeyData.key || 'YOUR_API_KEY'}" \<br />
                        &nbsp;&nbsp;-d '{`{"script": "..."}`}'
                      </div>
                    </div>
                    <div className="bg-background border border-border rounded p-3 font-mono text-xs overflow-x-auto">
                      <div className="text-green-400"># Using Python</div>
                      <div className="mt-1">
                        import requests<br />
                        <br />
                        headers = {`{"Authorization": "Bearer ${apiKeyData.key || 'YOUR_API_KEY'}"}`}<br />
                        response = requests.post(<br />
                        &nbsp;&nbsp;"https://your-deployment.gpu.com/execute",<br />
                        &nbsp;&nbsp;headers=headers,<br />
                        &nbsp;&nbsp;json={`{"script": "..."}`}<br />
                        )
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => setShowApiKeyModal(null)}
                    className="px-4 py-2 bg-background-tertiary border border-border text-foreground rounded-lg hover:bg-background-hover transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-foreground-secondary">Failed to load API key</p>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
